import { makeAutoObservable } from "mobx";
import { apiTypes } from "../../../services";
import { api } from "../../../services";
import { AttributeShelf, FetchShelf, LoaderShelf } from "../../../shelves";
import type { AnticipationStore, AuthenticationStore, FintechRegisterStore, PaymentInfo, ValidationOTPStore } from "../../../stores";
import { TabSelectedEnum } from "../../../stores";
import { getPublicIp } from "../../../services/GetPublicIP";
import platform from "platform";
import type { ValidateCodeOTPReq } from "../../../services/APIService/types";
import { DDI } from "../../../constants";
import { onlyNumber } from "../../../utils/formatters";

export class Store {
	public loader = new LoaderShelf();
	public attempts = new AttributeShelf(3);
	public password = new AttributeShelf("");
	public location = new AttributeShelf<{ lat: number | null; long: number | null }>({ lat: null, long: null });
	public postOtpValidate: FetchShelf<apiTypes.ValidateCodeOTPRes>;
	public postBalanceInquiryRequest: FetchShelf<apiTypes.BalanceInquiryRequest>;
	public postContractSignature: FetchShelf<apiTypes.SignatureData>;
	public getBalanceInquiryRequest: FetchShelf<apiTypes.BalanceInquiryRequest>;
	public isPulling = new AttributeShelf(false);
	public validationId = new AttributeShelf("");
	public codeOtp = new AttributeShelf("");
	public timeoutID = new AttributeShelf<NodeJS.Timeout | null>(null);

	constructor(
		public authentication: AuthenticationStore,
		public anticipationStore: AnticipationStore,
		public paymentInfo: PaymentInfo,
		public otpStore: ValidationOTPStore,
		public fintechRegisterStore: FintechRegisterStore,
		onSuccess: VoidFunction,
	) {
		const authToken = authentication.getToken() || "";
		const tabSelected = anticipationStore.getTabSelected();
		const isPartial = tabSelected === TabSelectedEnum.PARTIAL;
		const amount = isPartial
			? anticipationStore.partialAnticipationShelf.partialSimulationData.value?.disbursedIssueAmount
			: anticipationStore.totalAnticipationShelf.disbursedIssueAmount.value;
		const yearsAdvance = isPartial
			? anticipationStore.partialAnticipationShelf.partialSimulationData.value?.yearsInAdvance
			: anticipationStore.totalAnticipationShelf.yearsAdvance.value;

		this.postOtpValidate = new FetchShelf(
			async () => {
				const ip = await getPublicIp();
				const hashID = this.authentication.sessionId.value || "";
				const body: ValidateCodeOTPReq = {
					id: otpStore.requestIdOTP.value,
					token: this.codeOtp.value,
					authenticity: {
						timestamp: new Date().toISOString(),
						ipAddress: ip,
						fingerprint: hashID,
						sessionId: hashID,
					},
				};
				const validationData = await api.postValidateOTP(authToken, body);
				this.validationId.set(validationData.validationId);

				return validationData;
			},
			async () => {
				await this.postBalanceInquiryRequest.fetch();
			},
			() => {
				const remaningAttempts = this.attempts.value - 1;
				this.attempts.set(remaningAttempts);
			},
		);

		this.postBalanceInquiryRequest = new FetchShelf(
			async () => api.postBalanceInquiryRequest(authToken),
			async () => {
				this.isPulling.set(true);
				this.httpPulling();
			},
		);

		this.getBalanceInquiryRequest = new FetchShelf(
			async () => api.getBalanceInquiryRequest(this.authentication.getToken() || ""),
			async(response) => {
				if (response.status === apiTypes.BalanceInquiryRequestStatusEnum.failed) {
					if (response.error) {
						const remaningAttempts = this.attempts.value - 1;
						this.attempts.set(remaningAttempts);
					}
				}

				if (response.status === apiTypes.BalanceInquiryRequestStatusEnum.success) {
					clearInterval(this.timeoutID.value!);
					await this.postContractSignature.fetch();
				}
			},
		);

		this.postContractSignature = new FetchShelf(
			async () => {
				const ip = await getPublicIp();
				const hashID = this.authentication.sessionId.value || "";
				const sellerId = anticipationStore.sellerIdSelected.value;

				const pixKey = this.sanitizePixKey();
				const body: apiTypes.SignatureData = {
					disbursementBankAccount: {
						pixKey,
						pixTransferType: "key",
					},
					simulationDetails: {
						type: tabSelected,
						amount: amount || 0,
						yearsInAdvance: yearsAdvance || 0,
					},
					signatures: [
						{
							signedObject: {
								rawText: "user trying sign contract",
							},
							authenticity: {
								timestamp: new Date().toISOString(),
								ipAddress: ip,
								sessionId: hashID,
								fingerprint: {
									lat: this.location.value.lat,
									long: this.location.value.long,
									name: platform.product,
									model: platform.product,
									systemName: platform.os?.toString(),
									orientation: screen.availHeight > screen.availWidth ? "Landscape" : "Portrait",
									languageCode: window.navigator.languages[0],
									systemVersion: platform.version,
									userinterface: platform.product,
									localizedModel: platform.product,
									preferredLanguages: window.navigator.languages[0],
								},
							},
						},
					],
					seller: {
						sellerId,
					},
				};

				return api.postContractSignature(body, this.getValidationId, authToken);
			},
			() => {
				this.isPulling.set(false);
				onSuccess();
			},
		);
		makeAutoObservable(this);
	}

	public get isLoading(): boolean {
		return this.isPulling.value
			|| this.postOtpValidate.loader.isLoading
			|| this.postBalanceInquiryRequest.loader.isLoading
			|| this.getBalanceInquiryRequest.loader.isLoading
			|| this.postContractSignature.loader.isLoading;
	}

	public get getValidationId(): string {
		return this.validationId.value;
	}

	public sanitizePixKey(): string {
		const pixKey = this.paymentInfo.pixKey.value;
		const type = this.paymentInfo.pixKeyType.value;

		if (type === "cpf") {
			return onlyNumber(pixKey);
		}

		if (type === "cellPhone") {
			return DDI.BR + onlyNumber(pixKey);
		}

		return pixKey;
	}

	private httpPulling(): void {
		const timeoutId = setInterval(() => {
			this.getBalanceInquiryRequest.fetch();
		}, 1500);

		this.timeoutID.set(timeoutId);

		setTimeout(() => {
			clearInterval(this.timeoutID.value!);
		}, 10000);
	}
}
